.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  border-radius: var(--radius-1);
  font-family: inherit;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.005em;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background-color var(--transition-fast) var(--ease-out),
    border-color var(--transition-fast) var(--ease-out),
    color var(--transition-fast) var(--ease-out),
    box-shadow var(--transition-fast) var(--ease-out),
    opacity var(--transition-fast) var(--ease-out);
}

/* Keyboard-first focus: cyan glow ring (box-shadow) + a transparent outline so
   the ring still shows in forced-colors / high-contrast mode. --glow-focus is
   intentionally NOT dampened under reduced-motion (accessibility hard rule). */
.button:focus-visible {
  outline: 2px solid transparent;
  outline-offset: var(--focus-ring-offset);
  box-shadow: var(--glow-focus);
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sizeSm {
  min-height: var(--control-sm);
  padding: 0 10px;
  font-size: var(--font-size-2);
}

.sizeMd {
  min-height: var(--control-md);
  padding: 0 14px;
  font-size: var(--font-size-3);
}

.sizeLg {
  min-height: var(--control-lg);
  padding: 0 18px;
  font-size: var(--font-size-4);
}

/* Primary — filled cyan, navy text, glow on hover. */
.variantPrimary {
  background: var(--accent);
  color: var(--bg-base);
  border-color: var(--accent);
  font-weight: var(--font-weight-semibold);
}

.variantPrimary:hover:not(:disabled) {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  box-shadow: var(--glow-med);
}

.variantPrimary:active:not(:disabled) {
  background: var(--accent);
  box-shadow: var(--glow-low);
}

/* Secondary — cyan outline, transparent fill, faint glow on hover. */
.variantSecondary {
  background: transparent;
  color: var(--text-primary);
  border-color: color-mix(in srgb, var(--accent) 40%, transparent);
}

.variantSecondary:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent-strong);
  box-shadow: var(--glow-low);
}

/* Tertiary / ghost — dim, no chrome; full opacity on hover, no glow. */
.variantGhost {
  background: transparent;
  color: var(--text-secondary);
  opacity: 0.7;
}

.variantGhost:hover:not(:disabled) {
  opacity: 1;
  color: var(--text-primary);
}

/* Destructive — red outline + red glow on hover. */
.variantDanger {
  background: transparent;
  color: var(--state-negative);
  border-color: color-mix(in srgb, var(--state-negative) 45%, transparent);
}

.variantDanger:hover:not(:disabled) {
  border-color: var(--state-negative);
  color: color-mix(in srgb, var(--state-negative) 85%, white);
  box-shadow: 0 0 12px color-mix(in srgb, var(--state-negative) 40%, transparent);
}

/* The cyan glows flatten to none via tokens.css under reduced motion; flatten
   the custom red danger glow to match. */
@media (prefers-reduced-motion: reduce) {
  .variantDanger:hover:not(:disabled) {
    box-shadow: none;
  }
}
